import React, { useContext, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import Typography from 'kingpin/atoms/Typography';

import MetricCacheContext from 'contexts/MetricCacheContext';
import CardContext from 'contexts/CardContext';
import useGetMetricIdsFromVis from 'hooks/useGetMetricIdsFromVis';

import isV5ChartDef from '../../types/visTypeCheckers/isV5ChartDef';
import V5Gadget from '../V5Gadget';
import ErrorBoundary from '../Common/ErrorBoundary';
import isSingleMetricDateMatrix from '../../types/visTypeCheckers/isSingleMetricDateMatrix';
import SingleMetricDateMatrixContainer from '../SingleMetricDateMatrix';
import isGauge from '../../types/visTypeCheckers/isGauge';
import Gauge from '../Gauge';
import isRankingMatrix from '../../types/visTypeCheckers/isRankingMatrix';
import RankingMatrix from '../RankingMatrix';
import Card from '../Common/Card';
import isPaceMatrix from '../../types/visTypeCheckers/isPaceMatrix';
import PaceMatrix from '../PaceMatrix';
import isGroupVsRemainder from '../../types/visTypeCheckers/isGroupVsRemainder';
import GroupVsRemainder from '../GroupVsRemainder';
import isEntityInformationTable from '../../types/visTypeCheckers/isEntityInformationTable';
import EntityInformationTable from '../EntityInformationTable';
import Row from '../Common/Row';
import FilterPlates from '../FilterPlates';
import Loading from '../Loading/Loading';
import DateFieldPicker from '../DateInput/DateFieldPicker';
import ComparisonSelector from '../Report/ComparisonSelector';
import Inputs from '../Inputs';
import { Label } from '../MyAccount/Profile/styles';
import { Z_INDEX } from '../../constants';
import Checkbox from '../Inputs/Checkbox';
import Tooltip from '../Tooltip';
import Icon from '../../kingpin/atoms/Icon';

import EntityAppUsageAlert from '../Report/EntityAppUsageAlert';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: 24px;
  position: relative;
`;

const DEFAULT_DATASET_FILTERS_OPTIONS: {
  label: string;
  value: 'report' | 'enabled' | 'disabled';
}[] = [
  { label: 'Use Report Setting', value: 'report' },
  { label: 'Enabled', value: 'enabled' },
  { label: 'Disabled', value: 'disabled' },
];

const ConfigureReportGadget = ({
  chartDef,
  isValid,
  isDefaultFiltersDisabled,
  setIsDefaultFiltersDisabled,
  defaultDatasetFiltersMode,
  setDefaultDatasetFiltersMode,
}: {
  chartDef?: VisualisationDefinition;
  isValid: boolean;
  isDefaultFiltersDisabled: boolean;
  setIsDefaultFiltersDisabled: (value: boolean) => void;
  defaultDatasetFiltersMode: 'report' | 'enabled' | 'disabled';
  setDefaultDatasetFiltersMode: (
    value: 'report' | 'enabled' | 'disabled',
  ) => void;
}) => {
  const { requestMetricsByIds, isIdResolved } = useContext(MetricCacheContext);
  const getMetricIds = useGetMetricIdsFromVis();

  const metricIds = useMemo(
    () => (chartDef ? getMetricIds(chartDef) : []),
    [chartDef, getMetricIds],
  );

  useEffect(() => {
    if (metricIds.length > 0) {
      requestMetricsByIds(metricIds);
    }
  }, [metricIds, requestMetricsByIds]);

  const metricsResolved =
    metricIds.length === 0 || metricIds.every((id) => isIdResolved(id));

  return (
    <Wrapper
      onClick={(event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        event.stopPropagation();
      }}
    >
      <div>
        <div style={{ marginBottom: 24 }}>
          <Row style={{ marginBottom: 4 }}>
            <div style={{ marginRight: 16 }}>
              <DateFieldPicker />
            </div>
            {!isRankingMatrix(chartDef) && (
              <div style={{ marginRight: 16 }}>
                <Label>
                  <Typography.Body type={'Label'}>Compare With</Typography.Body>
                </Label>
                <ComparisonSelector isReport />
              </div>
            )}
            <div style={{ marginRight: 16 }}>
              <Label>
                <Typography.Body type={'Label'}>
                  Default Dataset Filters
                </Typography.Body>
              </Label>
              <Inputs.Dropdown
                options={DEFAULT_DATASET_FILTERS_OPTIONS.map((opt) => ({
                  label: opt.label,
                  onSelected: () => setDefaultDatasetFiltersMode(opt.value),
                }))}
                selectedLabel={
                  DEFAULT_DATASET_FILTERS_OPTIONS.find(
                    (opt) => opt.value === defaultDatasetFiltersMode,
                  )?.label || 'Use Report Setting'
                }
                unsetWidth
              />
            </div>
            <div style={{ marginLeft: 'auto' }}>
              <EntityAppUsageAlert />
            </div>
          </Row>
        </div>

        <div style={{ marginBottom: 24 }}>
          <div
            style={{
              marginBottom: 16,
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <Checkbox
              isChecked={isDefaultFiltersDisabled}
              onToggled={setIsDefaultFiltersDisabled}
            />
            <Typography.Body type="Body 12">
              Override report filters (use only gadget-specific filters)
            </Typography.Body>
            <Tooltip
              content={
                <div style={{ maxWidth: 320 }}>
                  <div>
                    When checked, this gadget ignores all report-level filters
                    (drilldowns, scope, default dataset filters, and any filters
                    passed when the report is opened as a popup) and uses only
                    its own gadget-specific filters. Useful for reference
                    gadgets that should always show global data.
                  </div>
                  <div style={{ marginTop: 8 }}>
                    Entity app, mapped view, and engagement portal filters are
                    applied independently and are not affected by this setting.
                  </div>
                </div>
              }
            >
              <Icon icon="info" color={'var(--color-grey-3)'} />
            </Tooltip>
          </div>

          <Row
            style={{
              zIndex: Z_INDEX.CONFIGURE_DASHBOARD_GADGET,
              flexWrap: 'wrap',
            }}
          >
            <div>
              <Label>
                <Row centerAlign style={{ gap: 4 }}>
                  <Typography.Body type={'Label'}>Filters</Typography.Body>
                  <Tooltip
                    content={
                      <div style={{ maxWidth: 280 }}>
                        Filters always applied to this gadget. Never shown to
                        viewers as dismissible plates — they are applied
                        silently on top of any active report-level filters.
                      </div>
                    }
                  >
                    <Icon icon="info" color={'var(--color-grey-3)'} />
                  </Tooltip>
                </Row>
              </Label>
              <Row>
                <FilterPlates isScope isDashboardCard label="Add Filter" />
              </Row>
            </div>
          </Row>
        </div>

        <Card style={{ position: 'relative', height: 400 }}>
          {!chartDef ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
              }}
            >
              <Typography.Body type={'Body 14'}>
                Select a gadget type to preview
              </Typography.Body>
            </div>
          ) : !metricsResolved ? (
            <Loading />
          ) : (
            <CardContext.Provider
              value={{
                chartDefinition: chartDef,
                i: 'na',
                w: 10,
                h: 10,
                isForConfigureDashboardGadgetPopup: true,
              }}
            >
              <ErrorBoundary>
                <>
                  {isV5ChartDef(chartDef) && (
                    <V5Gadget
                      chartDefinition={chartDef}
                      useSmallNoDataMessage={false}
                      key={chartDef.simpleGridConfig ? undefined : chartDef.id}
                    />
                  )}
                  {isSingleMetricDateMatrix(chartDef) && (
                    <SingleMetricDateMatrixContainer
                      gadget={chartDef}
                      key={chartDef.id}
                    />
                  )}
                  {isGauge(chartDef) && <Gauge gauge={chartDef} />}
                  {isRankingMatrix(chartDef) && (
                    <RankingMatrix gadget={chartDef} key={chartDef.id} />
                  )}
                  {isPaceMatrix(chartDef) && (
                    <PaceMatrix gadget={chartDef} key={chartDef.id} />
                  )}
                  {isGroupVsRemainder(chartDef) && (
                    <GroupVsRemainder gadget={chartDef} key={chartDef.id} />
                  )}
                  {isEntityInformationTable(chartDef) && (
                    <EntityInformationTable
                      gadget={chartDef}
                      key={Math.random()}
                    />
                  )}
                </>
              </ErrorBoundary>
            </CardContext.Provider>
          )}
        </Card>
      </div>
    </Wrapper>
  );
};

export default ConfigureReportGadget;
